import { useEffect, useRef, useState, useCallback } from "react";
import useAuth from "../context/AuthProvider";
import {
  StateReportModel,
  StateReportModel_Resp_Waiting_On_Client,
} from "../models/StateReportModel";
import { Box } from "@mui/system";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { MenuDropdown } from "../components/Elements/MenuDropdown";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Select,
  SelectProps,
} from "@mui/material";
import { writeXlsxFile } from "../helper/utils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { StateReportList } from "../components/Lists/StateReportList";
import Swal from "sweetalert2";
import moment from "moment";
import StateReportMarkCompleted from "./StateReportMarkCompleted";
import { apiClient } from "../helper/api";
import formatPrice from "../helper/currencyFormatter";
import StateReportFilerNotes from "../components/Elements/stateReportFilerNotes";
import { AddEditStateReport } from "../components/AddEdit/AddEditStateReport";

export enum StateReportPageTabs {
  Outstanding = 1,
  Completed = 2,
}

interface ReportItem {
  dueDate?: string | null;
  setDueDate?: string | null;
}

export const StateReport = () => {
  const { user } = useAuth();
  const token = user?.token;
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState<number>(1);
  const [filterModel, setFilterModel] = useState<{
    items: { columnField: string; value: string; operatorValue: string }[];
  }>({ items: [] });
  const [sortModel, setSortModel] = useState<{ field: string; sort: string }[]>(
    [],
  );
  const [itemList, setItemList] = useState<StateReportModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRows, setSelectedRows] = useState<StateReportModel[]>();
  const [openCompletedDialog, setOpenCompletedDialog] = useState<string>("");
  const [openNotesDialog, setOpenNotesDialog] = useState<boolean>(false);
  const [currentStatus, setCurrentStatus] = useState<string>("");
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [newStateReportData, setNewStateReportData] = useState<
    StateReportModel | undefined
  >(undefined);

  // Filter Options & values
  const [partners, setPartners] = useState([
    { partnersId: 0, customerName: "" },
  ]);
  const [selectedPartner, setSelectedPartner] = useState<string>("0");
  const [states, setStates] = useState([{ stateId: 0, stateName: "" }]);
  const [selectedState, setSelectedState] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedDueDate, setSelectedDueDate] = useState<string>("");
  const [dueDates, setDueDates] = useState<ReportItem[]>([]);

  // Refs for filter values
  const selectedPartnerRef = useRef<string>("0");
  const selectedStateRef = useRef<number>(0);
  const selectedStatusRef = useRef<string>("");
  const selectedDueDateRef = useRef<string>("");
  
  // Ref to hold AbortController for cancelling pending requests
  const controllerRef = useRef<AbortController | null>(null);
  
  // Ref to track previous selectedTab to prevent unnecessary calls
  const prevSelectedTabRef = useRef<number>(selectedTab);

  const statusFilterOptions = {
    1: [
      "Not Started",
      "Waiting on Client",
      "No Report Required",
      "Filed",
      "Revised",
    ], // Outstanding tab - includes all statuses for filtering
    2: ["Filed", "No Report Required"], // Completed tab - only show completed statuses
  };

  const statusActionOptions = {
    1: ["Not Started", "Waiting on Client", "No Report Required"], // Outstanding tab - all actionable statuses
    2: ["Not Started", "Waiting on Client", "No Report Required", "Revised"], // Completed tab - all actionable statuses
  };

  const exportOptions = ["Short", "Detailed"];
  const exportOptionsAdmin = ["Short", "Detailed", "All"];

  const keyNames: (keyof StateReportModel_Resp_Waiting_On_Client)[] = [
    "stateName",
    "name",
    "period",
    "dueDate",
    "payment",
    "report",
    "paymentType",
    "wetSignature",
    "notaryStamp",
    "licensee",
    "licenseNumber",
    "client",
    "mainLicensee",
    "zeroIndividual",
    "zeroEntity",
    "status",
    "dateFinalReportSentToClient",
    "largeReport",
    "firstRequest",
    "secondRequest",
    "thirdRequest",
    "notes",
    "taxAmount",
    "taxReported",
    "filerName",
  ];

  // API Functions
  const getStateReports = useCallback(
    async (pageNumber: number, _pageSize: number) => {
      // CANCEL previous pending request
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Create a new controller for this request
      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;
      const currentController = controllerRef.current; // Store reference to this request's controller

      setLoading(true);
      const requestBody = {
        pageSize: _pageSize || 50,
        currentPage: pageNumber || 1,
        partnerId: selectedPartnerRef.current || "",
        stateId: selectedStateRef.current || "",
        status: selectedStatusRef.current || "",
        dueDate: selectedDueDateRef.current || "",
        field: sortModel?.[0]?.["field"] || "",
        sort: sortModel?.[0]?.["sort"] || "",
        fieldName: filterModel?.items?.[0]?.["columnField"] || "",
        value: filterModel?.items?.[0]?.["value"] || "",
        operator: filterModel?.items?.[0]?.["operatorValue"] || "",
        selectedTab: selectedTab || 0,
      };

      // Debug: GET StateReport API call
      console.log("=== StateReport API Request Debug ===");
      console.log("Request Body:", JSON.stringify(requestBody, null, 2));
      console.log("Selected Tab:", selectedTab);
      console.log("User Roles:", user?.userRoles);

      let wasAborted = false;
      try {
        const res = await apiClient(`/StateReport/getStateReport`, {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify(requestBody),
          signal, // IMPORTANT: Pass abort signal
        });
        return res;
      } catch (err: any) {
        if (err.name === "AbortError") {
          wasAborted = true;
          return null; // Do not update state
        } else {
          console.error("API Error:", err);
          return null; // Do not update state on error
        }
      } finally {
        // Only set loading to false if this request wasn't aborted AND it's still the current request
        // This prevents canceled requests from hiding the loader for subsequent requests
        if (!wasAborted && controllerRef.current === currentController) {
          setLoading(false);
        }
      }
    },
    [token, sortModel, filterModel, selectedTab],
  );

  const fetchNewData = useCallback(async () => {
    const res = await getStateReports(currentPage, pageSize);

    // If aborted or no data, do nothing
    if (!res || !res.data) {
      setItemList([]);
      setTotalCount(0);
      return;
    }

    setItemList(res.data);
    setTotalCount(res.totalCount);
  }, [currentPage, pageSize, getStateReports]);

  const fetchRequiredData = useCallback(
    async (action: string): Promise<any[]> => {
      try {
        const response = await apiClient(action, {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
        });

        return response?.data || [];
      } catch (error) {
        console.error(`Error fetching ${action}:`, error);
        return [];
      }
    },
    [token],
  );

  const setRequiredStates = useCallback(async () => {
    try {
      const [partners, dueDates, states] = await Promise.all([
        fetchRequiredData("/Partners/getPartners"),
        fetchRequiredData("/StateReport/getDueDates"),
        fetchRequiredData("/States/getStates"),
      ]);

      setPartners(partners);
      setDueDates(dueDates);
      setStates(states || []);
    } catch (error) {
      console.error("Error setting required states:", error);
    }
  }, [fetchRequiredData]);

  // Event Handlers
  const handleSubmitCompletedDialog = (completedDate: string) => {
    setLoading(true);
    const selectedRowsArray: StateReportModel[] = [];
    selectedRows?.forEach((m) => {
      m.dateFinalReportSentToClient = completedDate;
      m.partnerId = m.partnersId;
      selectedRowsArray.push(m);
    });

    apiClient(`/StateReports/SaveStateReport`, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(selectedRowsArray),
    })
      .then((res) => {
        if (!res) throw new Error("Something went wrong");
        setSelectedRows([]);
        Swal.fire({
          title: "Successful!",
          text: "Success",
          icon: "success",
          confirmButtonText: "OK",
        });
        fetchNewData();
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  const handleSubmitStatus = (status: string) => {
    const selectedRowsArray: StateReportModel[] = [];
    if (
      status === "Waiting on Client" ||
      status === "Filed" ||
      status === "No Report Required"
    ) {
      setOpenNotesDialog(true);
      setCurrentStatus(status);
      return;
    }

    setLoading(true);

    if (status === "Revised") {
      // Handle Revised status: Create duplicates with "Revised - " prefix
      selectedRows?.forEach((m) => {
        const revisedRecord = {
          ...m, // Copy all properties from original
          stateReportId: 0, // Reset ID so it creates a new record
          status: "Revised", // New duplicate has "Revised" status for filtering
          name: `Revised - ${m.name}`, // Add "Revised - " prefix to report name
          partnerId: m.partnersId,
          // Reset completion fields for the new record
          dateFinalReportSentToClient: undefined,
          taxAmount: "0",
          notes: "",
          // Keep other important fields
          dueDate: m.dueDate,
          period: m.period,
          licensee: m.licensee,
          licenseNumber: m.licenseNumber,
          stateName: m.stateName,
          client: m.client,
          payment: m.payment,
          report: m.report,
          largeReport: m.largeReport,
        };
        selectedRowsArray.push(revisedRecord);
      });
    } else {
      // Handle other statuses normally
      selectedRows?.forEach((m) => {
        m.status = status;
        m.partnerId = m.partnersId;
        if (status != "Filed" && status != "No Report Required") {
          m.dateFinalReportSentToClient = "";
        }
        selectedRowsArray.push(m);
      });
    }

    updateStatusApi(selectedRowsArray);
  };

  const handleCloseNotesDialog = () => {
    setOpenNotesDialog(false);
  };

  // Add New State Report handlers
  const handleOpenAddDialog = () => {
    // Initialize with empty/default data for a new state report
    const defaultStateReport: StateReportModel = {
      stateReportId: 0,
      scheduleId: 0,
      stateName: "", // Will be selected by user
      licensee: "", // Will be selected by user based on client + state
      licenseNumber: "", // Will be populated when licensee is selected
      client: "", // Will be selected first by user
      partnersId: 0,
      name: "", // Report name starts blank - user enters
      largeReport: false,
      largeReportFee: "",
      status: "Not Started", // Default status, disabled in form
      dueDate: "",
      period: "", // Will be selected by user, filtered by state
      notes: "",
      taxAmount: "0",
      // Remove payment fields since they're set at universal level
      zeroEntity: "false",
      mainLicensee: "false",
    };
    setNewStateReportData(defaultStateReport);
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewStateReportData(undefined);
  };

  const handleSubmitAddDialog = () => {
    setOpenAddDialog(false);
    setNewStateReportData(undefined);
    // Refresh the data after adding a new state report
    fetchNewData();
  };

  const handleSubmitNotes = (data: any) => {
    setOpenNotesDialog(false);
    updateStatus(data);
  };

  const updateStatus = (data: any) => {
    setLoading(true);
    const {
      status,
      noteDetail,
      policySubStatus,
      dateDeclarationFiled,
      notesDate,
      suspended,
      taxAmount,
      dateFinalReportSentToClient,
      largeReport,
      largeReportFee,
    } = data;
    const newDate = new Date().toISOString().slice(0, 10);
    const noteDate = moment(newDate).format("MM/DD/YYYY");

    const selectedRowsArray: StateReportModel_Resp_Waiting_On_Client[] = [];
    selectedRows?.forEach((m) => {
      m.dateFinalReportSentToClient = dateFinalReportSentToClient;
      m.largeReport = largeReport;
      m.largeReportFee = largeReportFee;

      // Preserve the stateReportId from original record for UPDATE vs INSERT logic
      const stateReportIdValue = m.stateReportId;

      const extendedRow = {
        ...m,
        status,
        partnerId: m.partnersId,
        stateReportId: stateReportIdValue, // Use resolved stateReportId value
        filerName: `${user?.userFirstName} ${user?.userLastName || user?.userlastName}`,
        filerDate: noteDate,
        notes: noteDetail,
        policySubStatus,
        dateDeclarationFiled,
        firstRequest: data.firstRequestDate,
        secondRequest: data.secondRequestDate,
        thirdRequest: data.thirdRequestDate,
        notesDate,
        suspended,
        taxAmount,
        largeReportFee,
      } as StateReportModel_Resp_Waiting_On_Client;

      // StateReportId preserved for proper UPDATE/INSERT logic

      selectedRowsArray.push(extendedRow);
    });
    updateStatusApi(selectedRowsArray);
  };

  const updateStatusApi = async (data: any) => {
    try {
      // Check if we're creating new records (only for Revised status) or updating existing ones
      const isCreatingRevisedRecords = data.some(
        (record: any) =>
          record.status === "Revised" && record.stateReportId === 0,
      );
      // Check if this is a notes-related status update (has filerName or notes fields)
      const hasNotesData = data.some(
        (record: any) => record.filerName || record.notes,
      );

      // For notes-related updates, handle virtual vs existing records
      if (hasNotesData) {
        // Process the array to handle null stateReportId (virtual records)
        const processedData = data.map((record: any) => {
          if (
            record.stateReportId === null ||
            record.stateReportId === undefined ||
            record.stateReportId === 0
          ) {
            // Virtual record - needs INSERT, set stateReportId to 0
            return {
              ...record,
              stateReportId: 0,
            };
          }
          // Existing record - keep the existing stateReportId for UPDATE
          return record;
        });

        // Using AddEditStateReport endpoint for notes-related updates

        const res = await apiClient("/StateReport/AddEditStateReport", {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify(processedData),
        });

        if (res && res.status) {
          // CRITICAL FIX: Immediately update local state with returned stateReportId to prevent race condition
          if (res.data && Array.isArray(res.data)) {
            // Update itemList with the returned data containing correct stateReportIds
            setItemList((prevItemList) => {
              const updatedList = [...prevItemList];

              // For each returned record, update the corresponding item in our local list
              res.data.forEach((returnedRecord: any) => {
                const index = updatedList.findIndex(
                  (item) =>
                    item.rowNum === returnedRecord.rowNum ||
                    (item.partnersId === returnedRecord.partnersId &&
                      item.scheduleId === returnedRecord.scheduleId &&
                      item.stateName === returnedRecord.stateName),
                );

                if (index !== -1) {
                  // Update the existing record with the returned stateReportId
                  updatedList[index] = {
                    ...updatedList[index],
                    stateReportId:
                      returnedRecord.stateReportId ||
                      returnedRecord.StateReportId,
                    // Update other fields that might have changed
                    status: returnedRecord.status,
                    filerName: returnedRecord.filerName,
                    notes: returnedRecord.notes,
                    dateFinalReportSentToClient:
                      returnedRecord.dateFinalReportSentToClient,
                    taxAmount: returnedRecord.taxAmount,
                  };
                }
              });

              return updatedList;
            });

            // CRITICAL FIX: Also update selectedRows with the returned stateReportId
            setSelectedRows((prevSelectedRows) => {
              if (!prevSelectedRows) return prevSelectedRows;

              const updatedSelectedRows = [...prevSelectedRows];

              // For each returned record, update the corresponding item in selectedRows
              res.data.forEach((returnedRecord: any) => {
                const index = updatedSelectedRows.findIndex(
                  (item) =>
                    item.rowNum === returnedRecord.rowNum ||
                    (item.partnersId === returnedRecord.partnersId &&
                      item.scheduleId === returnedRecord.scheduleId &&
                      item.stateName === returnedRecord.stateName),
                );

                if (index !== -1) {
                  // Update the existing record with the returned stateReportId
                  updatedSelectedRows[index] = {
                    ...updatedSelectedRows[index],
                    stateReportId:
                      returnedRecord.stateReportId ||
                      returnedRecord.StateReportId,
                    // Update other fields that might have changed
                    status: returnedRecord.status,
                    filerName: returnedRecord.filerName,
                    notes: returnedRecord.notes,
                    dateFinalReportSentToClient:
                      returnedRecord.dateFinalReportSentToClient,
                    taxAmount: returnedRecord.taxAmount,
                  };
                }
              });

              return updatedSelectedRows;
            });
          }

          Swal.fire({
            title: "Success!",
            text: "Status updated successfully.",
            icon: "success",
            confirmButtonText: "OK",
          }).then(() => {
            setSelectedRows([]); // Clear selection to prevent stale data repopulation
            fetchNewData(); // Still refresh to ensure data consistency
          });
        } else {
          Swal.fire({
            title: "Error!",
            text: "Failed to update status.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
        return;
      }

      let endpoint;
      if (isCreatingRevisedRecords) {
        endpoint = "/StateReport/createRevisedStateReports";
      } else {
        endpoint = "/StateReport/updateStateReportsStatus";
      }

      // Using appropriate endpoint based on operation type

      const res = await apiClient(endpoint, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(data),
      });
      if (!res) throw new Error("Something went wrong");
      setSelectedRows([]);

      const successMessage = isCreatingRevisedRecords
        ? 'Revised state reports created successfully! They appear with "Revised" status and can be filtered accordingly.'
        : "Status updated successfully!";

      Swal.fire({
        title: `${res.status ? "Successful!" : "Error!"}`,
        text: `${res.status ? successMessage : res.message}`,
        icon: `${res.status ? "success" : "error"}`,
        confirmButtonText: "OK",
      }).then(() => {
        fetchNewData();
      });
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      return error;
    }
  };

  const onExportXlsx = (
    values: StateReportModel[],
    option: string = "Detailed",
  ) => {
    setLoading(true);
    if (values.length === 0) {
      return;
    }

    // Define different column sets for different export options
    const getExportColumns = (
      exportOption: string,
    ): (keyof StateReportModel_Resp_Waiting_On_Client)[] => {
      switch (exportOption) {
        case "Short":
          return [
            "stateName",
            "name",
            "period",
            "dueDate",
            "status",
            "client",
            "licensee",
            "licenseNumber",
          ];
        case "Detailed":
          return keyNames; // Use all columns
        case "All":
          return keyNames; // Use all columns (same as Detailed for now)
        default:
          return keyNames;
      }
    };

    const exportColumns = getExportColumns(option);

    const headers = exportColumns.map((key) => {
      switch (key) {
        case "stateName":
          return "state";
        case "name":
          return "reportName";
        case "payment":
          return "paymentRequired";
        case "report":
          return "reportRequired";
        case "wetSignature":
          return "wetSignatureRequired";
        case "notes":
          return "notesToClient";
        case "taxAmount":
          return "taxAmount";
        case "taxReported":
          return "taxReported";
        default:
          return key;
      }
    });

    const items = values.map((item) => {
      const update: (string | number | Date | undefined)[] = [];

      exportColumns.forEach(
        (key: keyof StateReportModel_Resp_Waiting_On_Client) => {
          if (
            key === "dueDate" ||
            key === "dateFinalReportSentToClient" ||
            key === "firstRequest" ||
            key === "secondRequest" ||
            key === "thirdRequest"
          ) {
            const dateValue = item[key as keyof StateReportModel];
            update.push(
              dateValue
                ? moment.utc(dateValue as string).format("MM/DD/YYYY")
                : "",
            );
          } else if (key === "taxReported" || key === "taxAmount") {
            const value = item[key as keyof StateReportModel] ?? "0";
            update.push(formatPrice(value as string));
          } else {
            update.push(
              item[key as keyof StateReportModel] as
                | string
                | number
                | undefined,
            );
          }
        },
      );

      return update;
    });

    const updates = [headers, ...items];

    // Generate filename based on export option
    const getFilename = (exportOption: string) => {
      switch (exportOption) {
        case "All":
          return "All_ExportedStateReport.xlsx";
        case "Short":
          return "Short_StateReport.xlsx";
        case "Detailed":
          return "Detailed_StateReport.xlsx";
        default:
          return "StateReport.xlsx";
      }
    };

    writeXlsxFile(updates, getFilename(option));
    setLoading(false);
  };

  const handleExport = (option: string) => {
    try {
      setLoading(true);

      // Export logic based on option
      if (option === "All") {
        // Export all data across all pages (ignores selected rows)
        // Use a large page size to ensure we get all records that match current filters
        getStateReports(1, totalCount || 10000)
          .then((response) => {
            const responseData = response.data;

            if (responseData && responseData.length > 0) {
              onExportXlsx(responseData, option);
              Swal.fire({
                title: "Export Successful!",
                text: `All ${responseData.length} records have been exported.`,
                icon: "success",
                confirmButtonText: "OK",
              });
            } else {
              alert("No data available for export");
            }
            setLoading(false);
          })
          .catch((error) => {
            setLoading(false);
            console.error("Error occurred during export:", error);
          });
      } else if (selectedRows && selectedRows.length > 0) {
        // Export selected rows for Short and Detailed options
        onExportXlsx(selectedRows, option);
        Swal.fire({
          title: "Export Successful!",
          text: `${selectedRows.length} selected records have been exported.`,
          icon: "success",
          confirmButtonText: "OK",
        });
        setLoading(false);
      } else {
        // Export only current page data for Short and Detailed options
        if (itemList && itemList.length > 0) {
          onExportXlsx(itemList, option);
          Swal.fire({
            title: "Export Successful!",
            text: `${itemList.length} records from current view have been exported.`,
            icon: "success",
            confirmButtonColor: "#02A4E3",
            confirmButtonText: "OK",
          });
        } else {
          alert("No data available for export");
        }
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error occurred during export:", error);
    }
  };

  const clearSearch = () => {
    setLoading(true);
    setSelectedState(0);
    setSelectedPartner("0");
    setSelectedStatus("");
    setSelectedDueDate("");
    selectedPartnerRef.current = "0";
    selectedStateRef.current = 0;
    selectedStatusRef.current = "";
    selectedDueDateRef.current = "";
    setLoading(false);
    fetchNewData();
  };

  // Custom Select Components
  const FMSelectCustomCustomer: React.FC<SelectProps> = () => (
    <div>
      <FormControl fullWidth>
        <Select
          onChange={(event) => {
            setSelectedPartner(event.target.value);
            selectedPartnerRef.current = event.target.value;
          }}
          value={selectedPartner}
          id="test-select-label"
          autoWidth
          size="small"
          label={null}
          className="border-round custom-menu-item"
          IconComponent={ExpandMoreIcon}
          displayEmpty
        >
          <MenuItem key={0} value={0} className="custom-menu-item">
            Client
          </MenuItem>
          {partners?.map((item, key) => (
            <MenuItem
              key={key}
              className="custom-menu-item"
              value={item.partnersId}
            >
              {item.customerName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );

  const FMSelectStateCustom: React.FC<SelectProps> = () => (
    <FormControl fullWidth>
      <Select
        onChange={(event) => {
          selectedStateRef.current = Number(event.target.value);
          setSelectedState(Number(event.target.value));
        }}
        value={selectedState}
        size="small"
        id="test-select-label"
        label={null}
        className="border-round custom-menu-item"
        IconComponent={ExpandMoreIcon}
        displayEmpty
      >
        <MenuItem value={"0"} className="custom-menu-item">
          Home State
        </MenuItem>
        {states?.map((state, key) => (
          <MenuItem
            key={key}
            value={state.stateId}
            className="custom-menu-item"
          >
            {state.stateName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const FMSelectStatus: React.FC<SelectProps> = () => (
    <div>
      <FormControl fullWidth>
        <Select
          onChange={(event) => {
            selectedStatusRef.current = event.target.value;
            setSelectedStatus(event.target.value);
          }}
          value={selectedStatus}
          id="status-select"
          autoWidth
          size="small"
          label={null}
          className="border-round custom-menu-item"
          IconComponent={ExpandMoreIcon}
          displayEmpty
        >
         {selectedTab === 2 && selectedRows && selectedRows.length > 0 && !selectedRows.some(row => 
            row.isDoneByClient === true || String(row.isDoneByClient) === "true"
          ) && <MenuItem key={""} value={""} className="custom-menu-item">
            Status
          </MenuItem>
          }
          {statusFilterOptions[
            selectedTab as keyof typeof statusFilterOptions
          ].map((item, key) => (
            <MenuItem key={key} className="custom-menu-item" value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );

  const FMSelectDate: React.FC<{ dueDates: ReportItem[] }> = ({ dueDates }) => (
    <FormControl fullWidth>
      <Select
        onChange={(event) => {
          setSelectedDueDate(event.target.value);
          selectedDueDateRef.current = event.target.value;
        }}
        value={selectedDueDate || ""}
        id="date-select-label"
        autoWidth
        size="small"
        className="border-round custom-menu-item"
        IconComponent={ExpandMoreIcon}
        displayEmpty
      >
        <MenuItem key="default" value="">
          Due Date
        </MenuItem>
        {dueDates?.map(
          (item, index) =>
            item.dueDate && (
              <MenuItem
                key={index}
                className="custom-menu-item"
                value={item.dueDate}
              >
                {item.dueDate
                  ? moment(item.dueDate).format("MM/DD/YYYY")
                  : "Invalid Date"}
              </MenuItem>
            ),
        )}
      </Select>
    </FormControl>
  );

  // Effects
  useEffect(() => {
    setRequiredStates();
  }, [setRequiredStates]);

  useEffect(() => {
    // Only run when tab actually changes
    if (prevSelectedTabRef.current === selectedTab) {
      return;
    }
    
    // Cancel any pending request when tab changes
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    
    // Update ref
    prevSelectedTabRef.current = selectedTab;
    
    setSelectedRows([]);
    setCurrentStatus("");
    setOpenNotesDialog(false);
    setOpenCompletedDialog("");
    setSelectedPartner("0");
    setSelectedState(0);
    setSelectedStatus("");
    setSelectedDueDate("");
    selectedPartnerRef.current = "0";
    selectedStateRef.current = 0;
    selectedStatusRef.current = "";
    selectedDueDateRef.current = "";
    setFilterModel({ items: [] });
    setSortModel([]);
    setCurrentPage(1);
    setPageSize(25);
    setTotalCount(0);
    setItemList([]);
    
  }, [selectedTab]);

  useEffect(() => {
    fetchNewData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, currentPage, sortModel, filterModel]);

  // Cleanup: Cancel any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="card-title">
            <span className="d-flex">State Report</span>
          </div>
        </div>
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            zIndex: "9990",
            width: "100%",
          }}
        >
          {loading && (
            <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />
          )}
        </div>
        <Box sx={{ width: "100%" }}>
          <Box>
            <div className="col-lg-12">
              <div
                className="d-flex"
                style={{ marginBottom: "28px" }}
                role="group"
                aria-label="Basic example"
              >
                <Grid
                  container
                  spacing={3}
                  className="filter-grid"
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="right"
                >
                  <div className="col-6 text-center">
                    <button
                      type="button"
                      className={`btn-tab btn ${selectedTab === 1 ? "btn-Selected" : "btn-primary1"}`}
                      onClick={() => setSelectedTab(1)}
                    >
                      Outstanding Queue
                    </button>
                  </div>
                  <div className="col-6 text-center">
                    <button
                      type="button"
                      className={`btn-tab btn ${selectedTab === 2 ? "btn-Selected" : "btn-primary"}`}
                      onClick={() => setSelectedTab(2)}
                    >
                      Completed Queue
                    </button>
                  </div>
                  <Box sx={{ width: "100%", marginTop: "25px" }}>
                    <Box>
                      <Grid
                        container
                        spacing={2}
                        className="filter-grid"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                          {(user?.userRoles
                            .split(",")
                            .includes("Super Admin") ||
                            user?.userRoles
                              .split(",")
                              .includes("MISC State Reporter")) && (
                            <FMSelectCustomCustomer />
                          )}
                        </Grid>
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                          <FMSelectStateCustom />
                        </Grid>
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                          <FMSelectStatus />
                        </Grid>
                        <Grid item xl={2} lg={4} sm={6} xs={12}>
                          <FMSelectDate dueDates={dueDates} />
                        </Grid>
                        <Grid item xl={0.7} sm={12} xs={12}>
                          <FormControl fullWidth>
                            <div>
                              <button
                                type="button"
                                onClick={fetchNewData}
                                className="active-btn1-blue-tabs"
                              >
                                Search
                              </button>
                            </div>
                          </FormControl>
                        </Grid>
                        <Grid item xl={0.7} sm={12} xs={12} alignItems="center">
                          <FormControl fullWidth>
                            <div>
                              <button
                                type="button"
                                onClick={clearSearch}
                                className="btn-clear-refresh btn"
                              >
                                Clear
                              </button>
                            </div>
                          </FormControl>
                        </Grid>
                        <Grid item xl={0.7} sm={12} xs={12} alignItems="center">
                          <FormControl fullWidth>
                            <div>
                              <button
                                type="button"
                                onClick={() => window.location.reload()}
                                className="btn-clear-refresh btn"
                              >
                                Refresh
                              </button>
                            </div>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                  {selectedRows?.length !== 0 &&
                    selectedRows?.length !== undefined &&
                    !(selectedTab === 2 && selectedRows.some(row => 
                      row.isDoneByClient === true || String(row.isDoneByClient) === "true"
                    )) && (
                      <Grid
                        item
                        minWidth={150}
                        className="select-input-border d-flex"
                      >
                        <MenuDropdown
                          menuTitle={"Status"}
                          items={
                            statusActionOptions[
                              selectedTab as keyof typeof statusActionOptions
                            ]
                          }
                          onItemClick={handleSubmitStatus}
                        />
                      </Grid>
                    )}
                  <Grid
                    item
                    xl={2}
                    sm={12}
                    xs={12}
                    minWidth={150}
                    className="select-input-border d-flex"
                  >
                    <button
                      onClick={handleOpenAddDialog}
                      className="active-btn-blue"
                      style={{ width: "100%", marginRight: "10px" }}
                    >
                      Add New
                    </button>
                  </Grid>
                  <Grid
                    item
                    xl={2}
                    sm={12}
                    xs={12}
                    minWidth={150}
                    className="select-input-border d-flex"
                  >
                    <MenuDropdown
                      menuTitle={"Export"}
                      items={
                        user?.userRoles.split(",").includes("Super Admin")
                          ? exportOptionsAdmin
                          : exportOptions
                      }
                      onItemClick={handleExport}
                    />
                  </Grid>
                </Grid>
              </div>
            </div>
          </Box>
        </Box>
        <Box>
          <StateReportFilerNotes
            open={openNotesDialog}
            handleClose={handleCloseNotesDialog}
            handleSubmit={handleSubmitNotes}
            selectedRows={selectedRows}
            currentStatus={currentStatus}
          />
        </Box>
      </LocalizationProvider>

      {openCompletedDialog && (
        <StateReportMarkCompleted
          open={!!openCompletedDialog}
          handleClose={() => setOpenCompletedDialog("")}
          handleSubmit={handleSubmitCompletedDialog}
          selectedRows={selectedRows}
        />
      )}

      <AddEditStateReport
        handleClose={handleCloseAddDialog}
        Open={openAddDialog}
        StateRuleMatrixCL={newStateReportData}
        handleSubmit={handleSubmitAddDialog}
        isStateReport={true}
      />

      {/* Debug: State values */}
      {console.log("=== StateReport Render Debug ===")}
      {console.log("itemList length:", itemList?.length)}
      {console.log("totalCount:", totalCount)}
      {console.log("loading:", loading)}
      {console.log("selectedTab:", selectedTab)}
      {console.log("===============================")}

      <StateReportList
        itemList={itemList}
        totalCount={totalCount}
        setFilterModel={setFilterModel}
        setSortModel={setSortModel}
        selectedTab={selectedTab}
        setSelectedRows={setSelectedRows}
        setCurrentPage={setCurrentPage}
        setPageSize={setPageSize}
        _currentPage={currentPage}
        _pageSize={pageSize}
        selectedRows={selectedRows}
        onRefresh={fetchNewData}
        states={states}
        setSelectedTab={setSelectedTab}
      />
    </>
  );
};
